//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { createContext, useContext } from 'react';
import {
  FieldPath,
  FieldPathValue,
  UseFormReturn,
  get,
  useForm,
} from 'react-hook-form';

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { getSocialNetworkFromPostType } from '@apis/universal-posts/universalPostsUtils';
import { SocialNetwork } from '@models/SocialNetwork';
import { useSchedulerSocialNetworksFromTabData } from '@universal-scheduling/components/scheduler/hooks/useSchedulerPostTypesFromTabData';
import { getDefaultPostDetailsFormState } from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormUtils';
import {
  PostDetailsFieldPath,
  PostDetailsFormKey,
  PostDetailsFormValues,
} from '@universal-scheduling/contexts/scheduler/postDetailsFormProviderModels';
import { getFormKey } from '@universal-scheduling/contexts/scheduler/utils/postDetailsFormContextUtils';
import {
  ISchedulerSharedFields,
  TSchedulerFormState,
} from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { SchedulerSocialNetwork } from '@universal-scheduling/models/SocialNetworkModels';
import { isDefined } from '@utils/filterPredicates';

type FieldKeyFunc<
  T extends SchedulerSocialNetwork,
  U extends TSchedulerFormState = PostDetailsFormValues[PostDetailsFormKey<T>],
> = <K extends FieldPath<U>>(name: K) => PostDetailsFieldPath<T, U, K>;

const makeGetFieldPath =
  <
    T extends SchedulerSocialNetwork,
    U extends
      TSchedulerFormState = PostDetailsFormValues[PostDetailsFormKey<T>],
  >(
    formId: string,
    formType: T,
  ): FieldKeyFunc<T, U> =>
  <K extends FieldPath<U>>(name: K): PostDetailsFieldPath<T, U, K> => {
    return `${formType}-${formId}.${name}`;
  };

type PostDetailsContextValues = UseFormReturn<PostDetailsFormValues> & {
  initializeFormValues: InitializeFormValues;
};

type InitializeFormValues = (
  formId: string,
  postType: UniversalPostType,
  postData?: TSchedulerFormState,
) => void;

interface PostDetailsContextValuesReturnBase
  extends UseFormReturn<PostDetailsFormValues> {
  getValueForAllForms: <K extends FieldPath<ISchedulerSharedFields>>(
    name: K,
  ) => ISchedulerSharedFields[K][];
  setValueForAllForms: <K extends FieldPath<ISchedulerSharedFields>>(
    name: K,
    value: ISchedulerSharedFields[K],
  ) => void;
  triggerFieldForAllForms: <K extends FieldPath<ISchedulerSharedFields>>(
    name: K,
  ) => void;
  initializeFormValues: InitializeFormValues;
}

interface PostDetailsContextValuesReturn<T extends SchedulerSocialNetwork>
  extends PostDetailsContextValuesReturnBase {
  formRootPath: PostDetailsFormKey<T>;
  getFieldPath: FieldKeyFunc<T>;
}

const PostDetailsFormContext = createContext<PostDetailsContextValues | null>(
  null,
);

const getActiveNetworkKeys = (
  formValues: Record<string, any>,
  activeNetworks: SocialNetwork[],
): string[] => {
  return Object.keys(formValues).filter((key) =>
    activeNetworks.some((network) => key.startsWith(`${network}-`)),
  );
};

const makeGetValueForAllForms =
  (
    form: UseFormReturn<PostDetailsFormValues>,
    activeNetworks: SocialNetwork[],
  ) =>
  <K extends FieldPath<ISchedulerSharedFields>>(name: K) => {
    const { getValues } = form;
    const formValues = getValues();
    const activeKeys = getActiveNetworkKeys(formValues, activeNetworks);

    return activeKeys.map(
      (key) => get(formValues, `${key}.${name}`) as ISchedulerSharedFields[K],
    );
  };

const makeSetValueForAllForms =
  (
    form: UseFormReturn<PostDetailsFormValues>,
    activeNetworks: SocialNetwork[],
  ) =>
  <K extends FieldPath<ISchedulerSharedFields>>(
    name: K,
    value: ISchedulerSharedFields[K],
  ) => {
    const { getValues, setValue } = form;
    const formValues = getValues();
    const activeKeys = getActiveNetworkKeys(formValues, activeNetworks);

    activeKeys.forEach((key) => {
      const path = `${key}.${name}` as `${keyof PostDetailsFormValues}.${K}`;
      setValue(
        path,
        value as FieldPathValue<PostDetailsFormValues, typeof path>,
      );
    });
  };

const makeTriggerFieldForAllForms =
  (
    form: UseFormReturn<PostDetailsFormValues>,
    activeNetworks: SocialNetwork[],
  ) =>
  <K extends FieldPath<ISchedulerSharedFields>>(name: K) => {
    const { getValues, trigger } = form;

    const formValues = getValues();
    const activeKeys = getActiveNetworkKeys(formValues, activeNetworks);

    activeKeys.forEach(async (key) => {
      const path = `${key}.${name}` as `${keyof PostDetailsFormValues}.${K}`;
      await trigger(path);
    });
  };

export function usePostDetailsFormContext<T extends SchedulerSocialNetwork>(
  formId: string,
  formType: T,
): PostDetailsContextValuesReturn<T>;
export function usePostDetailsFormContext(): PostDetailsContextValuesReturnBase;

export function usePostDetailsFormContext<T extends SchedulerSocialNetwork>(
  formId?: string,
  formType?: T,
): PostDetailsContextValuesReturn<T> | PostDetailsContextValuesReturnBase {
  const { ...form } = useContext(PostDetailsFormContext);

  const activeNetworks = useSchedulerSocialNetworksFromTabData();

  const getValueForAllForms = makeGetValueForAllForms(form, activeNetworks);
  const setValueForAllForms = makeSetValueForAllForms(form, activeNetworks);
  const triggerFieldForAllForms = makeTriggerFieldForAllForms(
    form,
    activeNetworks,
  );

  if (formId !== undefined && formType !== undefined) {
    return {
      ...form,
      formRootPath: getFormKey(formId, formType),
      getFieldPath: makeGetFieldPath<T>(formId, formType),
      getValueForAllForms,
      setValueForAllForms,
      triggerFieldForAllForms,
    };
  } else {
    return {
      ...form,
      getValueForAllForms,
      setValueForAllForms,
      triggerFieldForAllForms,
    };
  }
}

interface Props {
  children: React.ReactNode;
}

export const PostDetailsFormContextProvider = ({ children }: Props) => {
  const form = useForm<PostDetailsFormValues>({
    shouldUnregister: false,
    criteriaMode: 'all',
    mode: 'onTouched',
  });

  const initializeFormValues = (
    formId: string,
    postType: UniversalPostType,
    postData?: TSchedulerFormState,
  ) => {
    const { getValues, setValue } = form;

    const values = getValues();

    const initialPostData: TSchedulerFormState = postData
      ? postData
      : getDefaultPostDetailsFormState(postType);

    const network = getSocialNetworkFromPostType(initialPostData.postType);
    const formKey = getFormKey(formId, network);

    if (!values[formKey]) {
      setValue(formKey, { ...initialPostData, formId });
    } else if (
      !isDefined(values[formKey].postType) ||
      !values[formKey].formId
    ) {
      console.error(
        'invalid form state, postType value is',
        values[formKey].postType,
        'form id is',
        values[formKey].formId,
      );
    }
  };

  return (
    <PostDetailsFormContext.Provider value={{ ...form, initializeFormValues }}>
      {children}
    </PostDetailsFormContext.Provider>
  );
};
